import type { Technology } from '@graftcode/design-system'
import { Icon } from '@graftcode/design-system'

export function getCodeSnippetLanguage(technologyId: string | undefined): string {
  if (!technologyId) return 'javascript'
  const map: Record<string, string> = {
    npm: 'javascript',
    nuget: 'csharp',
    netcore: 'csharp',
    maven: 'java',
    pypi: 'python',
    python: 'python',
    packagist: 'php',
    ruby: 'ruby',
  }
  return map[technologyId] ?? 'plainText'
}

export const TECHNOLOGIES: Technology[] = [
  {
    id: 'npm',
    label: 'Npm',
    iconOnDark: Icon.DSNpmRegular,
    iconOnLight: Icon.DSNpmBlack,
  },
  {
    id: 'nuget',
    label: 'NuGet',
    iconOnDark: Icon.DSNugetRegular,
    iconOnLight: Icon.DSNugetBlack,
  },
  {
    id: 'maven',
    label: 'Maven',
    iconOnDark: Icon.DSMavenRegular,
    iconOnLight: Icon.DSMavenBlack,
  },
  {
    id: 'pypi',
    label: 'PyPi',
    iconOnDark: Icon.DSPythonRegular,
    iconOnLight: Icon.DSPypiBlack,
  },
  {
    id: 'mcp',
    label: 'MCP (Alpha)',
    iconOnDark: Icon.DSMcpRegular,
    iconOnLight: Icon.DSMcpBlack,
  },
  {
    id: 'packagist',
    label: 'Packagist',
    iconOnDark: Icon.DSPackagistRegular,
    iconOnLight: Icon.DSPackagistBlack,
  },
  {
    id: 'ruby',
    label: 'RubyGems',
    iconOnDark: Icon.DSRubyRegular,
    iconOnLight: Icon.DSRubyGemsBlack,
  },
]

export type TechnologySetupKeys = {
  id: string
  label: string
  path: string
}

export const TECHNOLOGY_SETUP_MAPPING: TechnologySetupKeys[] = [
  {
    id: 'npm',
    label: 'Npm',
    path: 'npm',
  },
  {
    id: 'maven',
    label: 'Apache Maven',
    path: 'maven',
  },
  {
    id: 'nuget',
    label: '.Net CLI',
    path: 'nuget',
  },
  {
    id: 'pypi',
    label: 'PyPi',
    path: 'pypi',
  },
  {
    id: 'packagist',
    label: 'Composer',
    path: 'packagist',
  },
  {
    id: 'ruby',
    label: 'RubyGems',
    path: 'rubygems',
  },
]

export const TECHNOLOGY_SETUP_COMMANDS: Record<string, Record<string, string>> = {
  npm: {
    Npm: 'npm install --registry {registry} {publishId}@{version}',
  },
  nuget: {
    '.Net CLI': 'dotnet add package -s {registry} {publishId} --version {version}',
  },
  maven: {
    'Apache Maven':
      '<dependency>\n\t<groupId>{mavenGroupId}</groupId>\n\t<artifactId>{mavenArtifactId}</artifactId>\n\t<version>{version}</version>\n</dependency>\n\n<!-- -------------- -->\n\n<repository>\n\t<id>graft-repository</id>\n\t<url>{registry}</url>\n</repository>',
  },
  pypi: {
    PyPi: 'python -m pip install --extra-index-url {registry} {publishId}=={version}',
  },
  packagist: {
    Composer: 'composer config repositories.graft composer {registry}\ncomposer require {publishId}:{version}',
  },
  ruby: {
    RubyGems: 'gem install {publishId} -v "{version}" --source "{registry}"',
  },
}

const ENV_VARIABLE_TEXT_TEMPLATE = 'name={graftName};runtime={runtime};host={protocol}://{gateway}/ws;stateless=true'

const ENV_VARIABLE_JSON_TEMPLATE = `{
  "configurations": {
    "{graftName}": {
      "runtime": "{runtime}",
      "host": "{protocol}://{gateway}/ws",
      "stateless": "true"
    }
  }
}`

const ENV_VARIABLE_YAML_TEMPLATE = `configurations:
  "{graftName}":
    runtime: {runtime}
    host: {protocol}://{gateway}/ws
    stateless: true`

const ENV_VARIABLE_DOTENV_TEMPLATE =
  'graftcode-config="name={graftName};runtime={runtime};host={protocol}://{gateway}/ws"'

const NPM_CONFIGURATIONS: Record<string, string> = {
  'In Code': 'import { GraftConfig } from "{publishId}";\nGraftConfig.host = "{protocol}://{gateway}/ws";',
  'ENV variable': ENV_VARIABLE_DOTENV_TEMPLATE,
  'Config file': ENV_VARIABLE_JSON_TEMPLATE,
  TXT: ENV_VARIABLE_TEXT_TEMPLATE,
  JSON: ENV_VARIABLE_JSON_TEMPLATE,
  YAML: ENV_VARIABLE_YAML_TEMPLATE,
}

const NUGET_CONFIGURATIONS: Record<string, string> = {
  'In Code': 'using {packageNamespace};\nGraftConfig.Host = "{protocol}://{gateway}/ws";',
  'ENV variable': ENV_VARIABLE_DOTENV_TEMPLATE,
  'Config file': ENV_VARIABLE_JSON_TEMPLATE,
  TXT: ENV_VARIABLE_TEXT_TEMPLATE,
  JSON: ENV_VARIABLE_JSON_TEMPLATE,
  YAML: ENV_VARIABLE_YAML_TEMPLATE,
}

const MAVEN_CONFIGURATIONS: Record<string, string> = {
  'In Code': 'import {packageNamespace}.GraftConfig;\nGraftConfig.host = "{protocol}://{gateway}/ws";',
  'ENV variable': ENV_VARIABLE_DOTENV_TEMPLATE,
  'Config file': ENV_VARIABLE_JSON_TEMPLATE,
  TXT: ENV_VARIABLE_TEXT_TEMPLATE,
  JSON: ENV_VARIABLE_JSON_TEMPLATE,
  YAML: ENV_VARIABLE_YAML_TEMPLATE,
}

const PYPI_CONFIGURATIONS: Record<string, string> = {
  'In Code': 'from {packageNamespace} import GraftConfig\nGraftConfig.host = "{protocol}://{gateway}/ws"',
  'ENV variable': ENV_VARIABLE_DOTENV_TEMPLATE,
  'Config file': ENV_VARIABLE_JSON_TEMPLATE,
  TXT: ENV_VARIABLE_TEXT_TEMPLATE,
  JSON: ENV_VARIABLE_JSON_TEMPLATE,
  YAML: ENV_VARIABLE_YAML_TEMPLATE,
}

const PACKAGIST_CONFIGURATIONS: Record<string, string> = {
  'In Code': 'GraftConfig::$host = "{protocol}://{gateway}/ws";',
  'ENV variable': ENV_VARIABLE_DOTENV_TEMPLATE,
  'Config file': ENV_VARIABLE_JSON_TEMPLATE,
  TXT: ENV_VARIABLE_TEXT_TEMPLATE,
  JSON: ENV_VARIABLE_JSON_TEMPLATE,
  YAML: ENV_VARIABLE_YAML_TEMPLATE,
}

const RUBY_CONFIGURATIONS: Record<string, string> = {
  'In Code': 'require "{rubyRequirePath}"\nGraftConfig.host = "{protocol}://{gateway}/ws"',
  'ENV variable': ENV_VARIABLE_DOTENV_TEMPLATE,
  'Config file': ENV_VARIABLE_JSON_TEMPLATE,
  TXT: ENV_VARIABLE_TEXT_TEMPLATE,
  JSON: ENV_VARIABLE_JSON_TEMPLATE,
  YAML: ENV_VARIABLE_YAML_TEMPLATE,
}

export const TECHNOLOGY_CONFIGURATIONS: Record<string, Record<string, string>> = {
  npm: NPM_CONFIGURATIONS,
  nuget: NUGET_CONFIGURATIONS,
  maven: MAVEN_CONFIGURATIONS,
  pypi: PYPI_CONFIGURATIONS,
  packagist: PACKAGIST_CONFIGURATIONS,
  ruby: RUBY_CONFIGURATIONS,
}

export const TECHNOLOGY_IMPORTS: Record<string, string> = {
  npm: 'import { {class} } from "{publishId}"',
  nuget: 'using {packageNamespace};',
  maven: 'import {packageNamespace}.{class};',
  pypi: 'from {packageNamespace} import {class}',
  packagist: 'use {phpNamespace}\\{class};',
  ruby: 'require "{rubyRequirePath}"',
}

export const TECHNOLOGY_LIBRARY_IMPORTS: Record<string, string> = {
  npm: 'export * from "{publishId}"',
  nuget: 'using {packageNamespace};',
  maven: 'import {packageNamespace}.*;',
  pypi: 'from {packageNamespace} import *',
  packagist: 'use GraftConfig;',
  ruby: 'require "{rubyRequirePath}"',
}

export const TECHNOLOGY_INSTANCES: Record<string, string> = {
  npm: 'const {name} = new {class}({params})',
  nuget: 'var {name} = new {class}({params})',
  maven: '{class} {name} = new {class}({params})',
  pypi: '{name} = {class}({params})',
  packagist: '${name} = new {class}({params})',
  ruby: '{name} = {class}.new({params})',
}

export const TECHNOLOGY_USAGES: Record<string, string> = {
  npm: '{class}.{method}({params})',
  nuget: '{class}.{method}({params})',
  maven: '{class}.{method}({params})',
  pypi: '{class}.{method}({params})',
  packagist: '${class}->{method}({params})',
  ruby: '{class}.{method}({params})',
}

export const TECHNOLOGY_FIELD_USAGES: Record<string, { static: string; instance: string; global: string }> = {
  npm: {
    static: '{class}.{field}',
    instance: '{name}.{field}',
    global: '{field}',
  },
  nuget: {
    static: '{class}.{field}',
    instance: '{name}.{field}',
    global: '{field}',
  },
  maven: {
    static: '{class}.{field}',
    instance: '{name}.{field}',
    global: '{field}',
  },
  pypi: {
    static: '{class}.{field}',
    instance: '{name}.{field}',
    global: '{field}',
  },
  packagist: {
    static: '{class}::{field}',
    instance: '${name}->{field}',
    global: '${field}',
  },
  ruby: {
    static: '{class}.{field}',
    instance: '{name}.{field}',
    global: '${field}',
  },
}

export const TECHNOLOGY_VARIABLES: Record<string, string> = {
  npm: 'const {name} = ',
  nuget: 'var {name} = ',
  maven: 'var {name} = ',
  pypi: '{name} = ',
  packagist: '${name} = ',
  ruby: '{name} = ',
}

export const TECHNOLOGY_ENDPOINT_PREFIXES: Record<string, string> = Object.fromEntries(
  TECHNOLOGY_SETUP_MAPPING.map(({ id, path }) => [id, `/${path}`])
)
